import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { RegisterService } from '../../../services/register.service';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';
import { UtilityService } from '../../../services/utility.service';
import { RegionListResponse, RegionResponse, UtilityResponse } from '../../../interfaces/responses/models';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { RegionService } from '../../../services/region.service';
import { positiveNumberValidator } from '../../../shared/validators/positive-number-validator';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit {
  registrationForm: FormGroup;
  states: RegionResponse[] = [];
  utilities: UtilityResponse[] = [];
  public error: any;
  isMobile: boolean = false;
  readonly customerCategories = ["Prosumer", "Consumer"]
  readonly meteringCategories = ["Net Metering", "Gross Metering"];

  constructor(
    private fb: FormBuilder,
    private route: Router,
    private localStorage: LocalStorageService,
    private utilityService: UtilityService,
    private userService: RegisterService,
    private screenSizeService: ScreenSizeService,
    private authService: AuthService,
    private regionService: RegionService,
  ) {
    const phone_number = this.localStorage.getPhoneNumber();
    this.registrationForm = this.fb.group({
      name: ['', [Validators.required ]],
      last_name: ['', [Validators.required ]],
      meter_number: ['',  Validators.required ],
      connection_number: ['',  Validators.required],
      state_id: ['', Validators.required],
      utility_id: ['', Validators.required],
      locality: ['', [Validators.required ]],
      phone_number: [{ value: phone_number, disabled: true }, [Validators.required]],
      pin_code: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      customer_category: ['', Validators.required],
      metering_category: ["No Category", Validators.required],
      solar_capacity: [null, [Validators.required, positiveNumberValidator()]],
    });
  }

  
  get state() {
    return this.registrationForm.get('state_id');
  }

  ngOnInit(): void {
    this.regionService.getStates().subscribe(
      (data: RegionListResponse) => {
        this.states = data.items;
      },
    );

    this.state?.valueChanges.subscribe((stateId: string) => {
      if (stateId) {
        this.getUtilityByState(stateId);
      }
    });

    this.screenSizeService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });
  }

  getUtilityByState(state_id: any) {
    this.utilityService.getUtlityByState(state_id).subscribe(
      data => {
        this.utilities = data.items;
      },
      error => {
        console.log("Error in fetching Utility");
      }
    )
  }
  
  get meterNumber() { return this.registrationForm.get('meter_number'); }
  get name() { return this.registrationForm.get('name'); }
  get last_name() { return this.registrationForm.get('last_name'); }
  get connection_number() { return this.registrationForm.get('connection_number'); }
  get utility_id() { return this.registrationForm.get('utility_id'); }
  get pin_code() { return this.registrationForm.get('pin_code'); }
  get locality() { return this.registrationForm.get('locality'); }
  get customer_category() { return this.registrationForm.get('customer_category'); }
  get solar_capacity() { return this.registrationForm.get('solar_capacity'); }
  get metering_category() { return this.registrationForm.get('metering_category'); }
  get phone_number() { return this.registrationForm.get('phone_number'); }

  async onSubmit(): Promise<void> {
    if (this.registrationForm.invalid) {
      console.log('Form is invalid', this.registrationForm.errors);
      return;
    }
    const formData = this.registrationForm.getRawValue();
    console.log('Form Submitted', formData);
    this.userService.registerUser(formData).subscribe(
      async response => {
        console.log('User registered successfully', response);
        await this.authService.setToken(response.session_token);
        this.authService.setUser(response.name);
        this.authService.setRole('Pending');
        
        // Use isMobile to redirect to the appropriate route
        const redirectTo = this.isMobile ? '/pending-registration-mobile' : '/pending';
        this.route.navigateByUrl(redirectTo);
      },
      error => {
        console.error('Error registering user', error);
        this.error = 'User Already Registered';
      }
    );
  }

  validatePinCode(): void {
    const pinCodeControl = this.pin_code;
    if (pinCodeControl && pinCodeControl.value) {
      if (pinCodeControl.value.length < 6) {
        pinCodeControl.setErrors({ 'incorrectLength': true });
      } else {
        pinCodeControl.setErrors(null);
      }
    }
  }
  
  onCancel(): void {
    this.registrationForm.reset();
    this.route.navigateByUrl('welcome')
  }

  navigateTo(path: string) {
    this.route.navigate([path]);
  }

  isProsumer(): boolean {
    return this.customer_category?.value === 'Prosumer';
  }

  updateValidatorsOnChange(): void {
    if (this.isProsumer()) {
      this.solar_capacity?.setValidators([Validators.required, positiveNumberValidator()]);
      this.metering_category?.setValidators([Validators.required]);
    } else {
      this.solar_capacity?.clearValidators();
      this.metering_category?.clearValidators();
    }
    this.solar_capacity?.updateValueAndValidity();
    this.metering_category?.updateValueAndValidity();
  }

  onCustomerCategoryChange(_: Event): void {
    const userType = this.customer_category?.value;
    console.log('User Type Changed', userType);
    this.updateValidatorsOnChange();
  }
}